/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AzureInfraInsightsPage.tsx
 *     Feature         :   217-insights-dashboards (T041 / US5, FR-007)
 *                         Analytics upgrade 2026-07-14 — real, grounded estate stats.
 *     Description     :   Azure Infrastructure Insights — the cross-subscription estate
 *                         census Azure won't draw. Every KPI tile carries an AppTooltip
 *                         explaining the number and its dbo source. All analytics render
 *                         from collected data (FR-014):
 *                           • Added (7d) is derived entity-first from
 *                             dbo.azure_resources.created_at (NOT the empty
 *                             infrastructure_sync_deltas table).
 *                           • Removed (7d) counts deleted_at, which the merge's presence pass
 *                             stamps when a subscription the worker can see stops listing a
 *                             resource. Rows the worker cannot see are not removed: they carry
 *                             no_access_since and are labelled No access (Spec 286 T159 item 3).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { Cloud, ShieldAlert, Info, PlusCircle } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, StatCard, AppTooltip, ErrorState, LoadingState,
  NotCollected, Extended, QuickGlance, chartTooltipStyle,
} from '../../components/ui';
import { NoAccessBadge, WorkerVisibilityNotice } from '../../components/collection/WorkerVisibility';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '../../utils/dateFormatter';
import { ResourceTypeTick } from './resourceTypeTick';

interface AzureInfraInsightsPayload {
  kpis: {
    subscriptionCount: number;
    subscriptionsWithResources: number;
    resourceGroupCount: number;
    resourceCount: number;
    resourceTypeCount: number;
    regionCount: number;
    addedThisWeek: number;
    removedThisWeek: number;
    noAccessSubscriptionCount: number;
    noAccessResourceCount: number;
    noAccessSince: string | null;
  };
  resourcesByType: Array<{ type: string; count: number }>;
  regionDistribution: Array<{ region: string; count: number }>;
  bySubscription: Array<{ name: string; count: number; typeCount: number; rgCount: number; noAccessSince: string | null }>;
  topResourceGroups: Array<{ resourceGroupId: string; name: string; subscriptionName: string | null; count: number }>;
  discoveryTimeline: Array<{ date: string; count: number }>;
  recentlyAdded: Array<{ resourceId: string; name: string; type: string; resourceGroupName: string | null; subscriptionName: string | null; at: string }>;
  publicExposure: Array<{ accountId: string; name: string; subscriptionName: string | null; noAccessSince: string | null }>;
  orphanCandidates: Array<{ resourceGroupId: string; name: string; resourceCount: number }>;
}

/** Trim the noisy "Microsoft." provider prefix off a resource type for chart axes. */
const shortType = (t: string): string => t.replace(/^microsoft\./i, '');

/** KPI tile label with an inline lucide Info icon (matches the WarehouseInsights pattern). */
function statLabel(text: string): React.ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      {text}
      <Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
    </span>
  );
}

export const AzureInfraInsightsPage: React.FC = () => {
  const query = useQuery<AzureInfraInsightsPayload>({
    queryKey: ['azure-infra-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: AzureInfraInsightsPayload }>('/api/azure-infra-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  // Top 12 resource types + an aggregated "Other" bucket for the bar chart.
  const typeChart = React.useMemo(() => {
    const rows = data?.resourcesByType ?? [];
    const top = rows.slice(0, 12).map((r) => ({ label: shortType(r.type), count: r.count }));
    const rest = rows.slice(12).reduce((s, r) => s + r.count, 0);
    return rest > 0 ? [...top, { label: `Other (${rows.length - 12} types)`, count: rest }] : top;
  }, [data]);

  const recentlyAddedColumns: DataTableColumn<AzureInfraInsightsPayload['recentlyAdded'][number]>[] = [
    { key: 'name', header: 'Resource', width: '30%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'type', header: 'Type', width: '25%', render: (r) => shortType(r.type) },
    { key: 'resourceGroupName', header: 'Resource Group', width: '20%', render: (r) => r.resourceGroupName ?? '—' },
    { key: 'subscriptionName', header: 'Subscription', width: '13%', render: (r) => r.subscriptionName ?? '—' },
    { key: 'at', header: 'First seen', width: '12%', align: 'right', render: (r) => formatEastern(r.at) },
  ];

  const subscriptionColumns: DataTableColumn<AzureInfraInsightsPayload['bySubscription'][number]>[] = [
    {
      key: 'name', header: 'Subscription', width: '46%', cellClassName: 'font-medium text-[var(--color-text-primary)]',
      render: (r) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {r.name}
          {r.noAccessSince && <NoAccessBadge since={r.noAccessSince} />}
        </span>
      ),
    },
    { key: 'count', header: 'Resources', width: '18%', align: 'right', render: (r) => r.count.toLocaleString() },
    { key: 'typeCount', header: 'Types', width: '18%', align: 'right' },
    { key: 'rgCount', header: 'Resource Groups', width: '18%', align: 'right' },
  ];

  const rgColumns: DataTableColumn<AzureInfraInsightsPayload['topResourceGroups'][number]>[] = [
    { key: 'name', header: 'Resource Group', width: '50%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'subscriptionName', header: 'Subscription', width: '30%', render: (r) => r.subscriptionName ?? '—' },
    { key: 'count', header: 'Resources', width: '20%', align: 'right', render: (r) => r.count.toLocaleString() },
  ];

  const exposureColumns: DataTableColumn<AzureInfraInsightsPayload['publicExposure'][number]>[] = [
    {
      key: 'name', header: 'Storage Account', width: '60%', cellClassName: 'font-medium text-[var(--color-text-primary)]',
      render: (r) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {r.name}
          {r.noAccessSince && <NoAccessBadge since={r.noAccessSince} />}
        </span>
      ),
    },
    { key: 'subscriptionName', header: 'Subscription', width: '40%', render: (r) => r.subscriptionName ?? '—' },
  ];

  const orphanColumns: DataTableColumn<AzureInfraInsightsPayload['orphanCandidates'][number]>[] = [
    { key: 'name', header: 'Resource Group', width: '70%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'resourceCount', header: 'Resources', width: '30%', align: 'right' },
  ];

  useAutoRefresh(refresh, { lastFetchedAt });

  return (
    <PageLayout
      title="Azure Infrastructure Insights"
      description="The estate map Azure won't draw — cross-subscription resource census with change awareness"
      icon={<Cloud className="h-6 w-6" />}
      helpId="insights.azure-infra"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load Azure infrastructure insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <WorkerVisibilityNotice
            since={data.kpis.noAccessSince}
            counts={[
              { noun: 'subscriptions', noAccess: data.kpis.noAccessSubscriptionCount, total: data.kpis.subscriptionCount },
              { noun: 'resources', noAccess: data.kpis.noAccessResourceCount, total: data.kpis.resourceCount },
            ]}
          />
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-6">
            <StatCard
              label={statLabel('Subscriptions')}
              value={data.kpis.subscriptionCount}
              subtext={`${data.kpis.subscriptionsWithResources} host resources`}
              tooltip="Distinct Azure subscriptions in the collected census (dbo.azure_subscriptions), including any the collection worker can no longer see, which are marked No access. Subtext = how many contain at least one resource."
            />
            <StatCard
              label={statLabel('Resource Groups')}
              value={data.kpis.resourceGroupCount}
              tooltip="Resource groups discovered across every subscription (dbo.azure_resource_groups, deleted_at IS NULL)."
            />
            <StatCard
              label={statLabel('Resources')}
              value={data.kpis.resourceCount.toLocaleString()}
              subtext={`${data.kpis.resourceTypeCount} distinct types`}
              tooltip="Total Azure resources in the census (dbo.azure_resources, deleted_at IS NULL), including any the collection worker can no longer see, which are counted as they were when last seen. Subtext = distinct resource_type values."
            />
            <StatCard
              label={statLabel('Regions')}
              value={data.kpis.regionCount}
              tooltip="Distinct Azure regions the estate spans (COUNT DISTINCT dbo.azure_resources.location)."
            />
            <StatCard
              label={statLabel('Added (7d)')}
              value={data.kpis.addedThisWeek}
              valueClassName={data.kpis.addedThisWeek > 0 ? 'text-[var(--color-status-success)]' : undefined}
              tooltip="Resources first seen in the census in the last 7 days (dbo.azure_resources.created_at). Derived entity-first from the resource table — not the change-log."
            />
            <StatCard
              label={statLabel('Removed (7d)')}
              value={data.kpis.removedThisWeek}
              tooltip="Resources that a subscription the collection worker can see stopped listing in the last 7 days (dbo.azure_resources.deleted_at). A resource the worker can no longer see is not counted here: it is marked No access."
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the type/region breakdowns, discovery timeline, and exposure lists.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              {/* Type + region breakdowns */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Resources by type</h2>
                    <AppTooltip title="Count of resources per Azure resource_type (dbo.azure_resources), top 12 with the remaining types aggregated into 'Other'.">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  {/* Spec 286 T159 item 21: two-line ticks (provider, then type) so a long type is not clipped. */}
                  <div style={{ height: Math.max(220, typeChart.length * 32) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={typeChart} layout="vertical" margin={{ left: 10 }}>
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="label" tick={<ResourceTypeTick />} width={190} interval={0} />
                        <Tooltip {...chartTooltipStyle} formatter={(v) => [Number(v).toLocaleString(), 'Resources']} />
                        <Bar dataKey="count" name="Resources" fill="var(--color-accent)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <Card>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Resources by region</h2>
                    <AppTooltip title="Geographic spread — count of resources per Azure region (dbo.azure_resources.location).">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  <div style={{ height: Math.max(220, data.regionDistribution.length * 26) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.regionDistribution} layout="vertical" margin={{ left: 10 }}>
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="region" tick={{ fontSize: 10 }} width={120} />
                        <Tooltip {...chartTooltipStyle} formatter={(v) => [Number(v).toLocaleString(), 'Resources']} />
                        <Bar dataKey="count" name="Resources" fill="var(--color-accent)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>

              {/* Discovery timeline */}
              <Card>
                <div className="mb-1 flex items-center gap-2">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Discovery timeline — last 30 days</h2>
                  <AppTooltip title="Resources first entering the census per day (dbo.azure_resources.created_at). Shows recent estate-growth activity; the initial bulk backfill is outside this 30-day window.">
                    <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                  </AppTooltip>
                </div>
                <p className="mb-3 text-xs text-[var(--color-text-secondary)]">New resources appearing in the estate each day</p>
                {data.discoveryTimeline.length > 1 ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.discoveryTimeline}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={40} allowDecimals={false} />
                        <Tooltip {...chartTooltipStyle} formatter={(v) => [Number(v).toLocaleString(), 'New resources']} />
                        <Line type="monotone" dataKey="count" name="New resources" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <NotCollected reason="Fewer than 2 days of new-resource discovery in the last 30 days" />
                )}
              </Card>

              {/* Subscription + resource-group density */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card padding="none">
                  <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Resources by subscription</h2>
                  <p className="px-4 text-xs text-[var(--color-text-secondary)]">Where the estate lives — resource, type, and RG density per subscription</p>
                  <DataTable columns={subscriptionColumns} rows={data.bySubscription} rowKey={(r) => r.name} pageSize={10} emptyMessage="No resources collected" />
                </Card>

                <Card padding="none">
                  <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Heaviest resource groups</h2>
                  <p className="px-4 text-xs text-[var(--color-text-secondary)]">Top 15 resource groups by resource count</p>
                  <DataTable columns={rgColumns} rows={data.topResourceGroups} rowKey={(r) => r.resourceGroupId} pageSize={10} emptyMessage="No resource groups collected" />
                </Card>
              </div>

              {/* Recently added — real, entity-first replacement for the empty drift feed */}
              <Card padding="none">
                <h2 className="flex items-center gap-1.5 px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">
                  <PlusCircle className="h-4 w-4 text-[var(--color-status-success)]" /> Recently added — last 7 days
                </h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Resources first discovered in the estate this week (dbo.azure_resources.created_at)</p>
                <DataTable columns={recentlyAddedColumns} rows={data.recentlyAdded} rowKey={(r) => r.resourceId} pageSize={10} emptyMessage="No new resources discovered in the last 7 days" />
              </Card>

              <Card padding="none">
                <h2 className="flex items-center gap-1.5 px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">
                  <ShieldAlert className="h-4 w-4 text-[var(--color-status-warning)]" /> Public-endpoint exposure
                </h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Storage accounts allowing public network access (SOX pressure)</p>
                <DataTable columns={exposureColumns} rows={data.publicExposure} rowKey={(r) => r.accountId} pageSize={10} emptyMessage="No publicly-exposed storage accounts" />
              </Card>

              <Card padding="none">
                <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Orphan candidates</h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Resource groups with exactly 1 resource</p>
                <DataTable columns={orphanColumns} rows={data.orphanCandidates} rowKey={(r) => r.resourceGroupId} pageSize={10} emptyMessage="No orphan candidates" />
              </Card>
            </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default AzureInfraInsightsPage;
